export type ProductPricePresentationProps = {
  taxPresentationType:
    | 'only_tax_included'
    | 'both_main_tax_included'
    | 'both_main_tax_excluded'
    | 'unused'
  taxIncludedPrice?: number | null
  taxExcludedPrice?: number | null
  pointKind?: 'times_point' | 'refund_point' | 'percent_refund_point' | null
  pointAmount?: number | null
  discountRatio?: number | null
}

export type makeProductPricePresentation = {
  main: ProductPriceMainPresentation[]
  sub?: { text: string }
}

export type ProductPriceMainPresentation = {
  size: 'large' | 'small'
  text: string
  code?: string
}

export default function makeProductPricePresentation(
  props: ProductPricePresentationProps
): makeProductPricePresentation {
  if (props.taxPresentationType !== 'unused') {
    return makePricedProductPricePresentation(props)
  } else if (props.discountRatio) {
    return makeDiscountedProductPricePresentation(props)
  } else if (props.pointKind) {
    return makePointProductPricePresentation(props)
  } else {
    return {
      main: [],
    }
  }
}

function makePricedProductPricePresentation({
  taxPresentationType,
  taxIncludedPrice,
  taxExcludedPrice,
}: ProductPricePresentationProps): makeProductPricePresentation {
  if (taxPresentationType === 'both_main_tax_excluded') {
    return {
      main: [
        {
          size: 'large',
          text: String(taxExcludedPrice),
        },
        {
          size: 'small',
          text: '円',
        },
      ],
      sub: {
        text: `${taxIncludedPrice}円(税込)`,
      },
    }
  } else if (taxPresentationType === 'both_main_tax_included') {
    return {
      main: [
        {
          size: 'large',
          text: String(taxIncludedPrice),
        },
        {
          size: 'small',
          text: '円',
        },
      ],
      sub: {
        text: `${taxExcludedPrice}円(税抜)`,
      },
    }
  } else if (taxPresentationType === 'only_tax_included') {
    return {
      main: [
        {
          size: 'large',
          text: String(taxIncludedPrice),
        },
        {
          size: 'large',
          text: '円(税込)',
          code: String.fromCharCode(0xf104),
        },
      ],
    }
  } else {
    throw new Error()
  }
}

function makeDiscountedProductPricePresentation({
  discountRatio,
}: ProductPricePresentationProps): makeProductPricePresentation {
  if (discountRatio === 0.5) {
    return {
      main: [
        {
          size: 'large',
          text: '半額',
        },
      ],
    }
  } else if (discountRatio) {
    return {
      main: [
        {
          size: 'large',
          text: `${discountRatio * 100}%引`,
        },
      ],
    }
  } else {
    throw new Error()
  }
}

function makePointProductPricePresentation({
  pointKind,
  pointAmount,
}: ProductPricePresentationProps): makeProductPricePresentation {
  if (pointKind === 'percent_refund_point') {
    return {
      main: [
        {
          size: 'large',
          text: `${pointAmount}%`,
        },
        {
          size: 'large',
          text: 'ポイントプレゼント',
          code: String.fromCharCode(0xf003),
        },
      ],
    }
  } else if (pointKind === 'refund_point') {
    return {
      main: [
        {
          size: 'large',
          text: `${pointAmount}`,
        },
        {
          size: 'large',
          text: 'ポイントプレゼント',
          code: String.fromCharCode(0xf003),
        },
      ],
    }
  } else if (pointKind === 'times_point') {
    return {
      main: [
        {
          size: 'small',
          text: 'ポイント',
          code: String.fromCharCode(0xf002),
        },
        {
          size: 'large',
          text: `${pointAmount}`,
        },
        {
          size: 'small',
          text: '倍',
        },
      ],
    }
  } else {
    throw new Error()
  }
}
